import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import {
  BrandApplicationForBusiness,
  approveBrandApplication,
  getCampaignApplications,
  rejectBrandApplication,
} from '../../api/payment/brand-deal.api';
import { applicationStatusLabel } from '../../utils/brand-deal';
import { showOrderErrorToast } from './order-error-toast';
import { useOrdererSanction } from '../brand-sanction/orderer-sanction-context';
import { SANCTION_LOCK_HINT } from '../../utils/brand-sanction';
import { BrandThreadToggle } from './brand-thread-panel';

/**
 * Tab "Đơn xin tham gia" (doanh nghiệp, Task 4): đơn creator xin tham gia sàn — [Duyệt & đăng] /
 * [Từ chối] (lý do tuỳ chọn) / [Thương lượng] (cha xử lý — mở form mời M1 điền sẵn creator, KHÔNG
 * gọi `reject` sau đó vì backend tự đóng đơn Pending khi lời mời riêng được gửi, global-constraints).
 */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const REJECT_REASON_MAX_LENGTH = 200;

export interface BrandCampaignApplicationsHandle {
  reload: () => void;
}

interface BrandCampaignApplicationsProps {
  campaignId: string;
  /** Duyệt/từ chối xong thì suất/tiền trong ký quỹ sàn hoặc số đơn chờ duyệt đổi — cha cần refetch
   * chi tiết chiến dịch (badge "Đơn xin tham gia (N)" dựa trên `pendingApplications` của chi tiết). */
  onChanged: () => void;
  onNegotiate: (application: BrandApplicationForBusiness) => void;
  /** M5 (Task 3): chuyển trang cha sang mục "Chờ chấm điểm" — hiện nút "Chấm điểm ngay" trên toast lỗi
   * "Bạn cần chấm điểm…" khi duyệt đơn bị chặn vì còn deal chưa chấm. */
  onGoToRatingsTab?: () => void;
}

// Khai ngoài component cha (landmine ghi nhớ: component khai trong render body gây remount cả
// subtree, mất trạng thái/dở dang) — 1 thẻ đơn xin tham gia.
const ApplicationCard: React.FC<{
  application: BrandApplicationForBusiness;
  busy: boolean;
  /** Đang bị khoá tạo việc: duyệt và thương lượng bị chặn, từ chối vẫn được (RB8). */
  locked: boolean;
  onApprove: () => void;
  onReject: () => void;
  onNegotiate: () => void;
}> = ({ application, busy, locked, onApprove, onReject, onNegotiate }) => {
  const { creator } = application;
  return (
    <div className="rounded-lg border ui-divider p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#EAEFFA] text-sm font-bold text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
            {creator.avatar ? (
              <img src={creator.avatar} alt="" className="size-full object-cover" />
            ) : (
              creator.displayName.trim()[0]?.toUpperCase() || '?'
            )}
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium ui-text">{creator.displayName}</div>
            <div className="truncate text-xs ui-muted">
              @{creator.userName} · {creator.followers.toLocaleString('vi-VN')} người theo dõi ·{' '}
              {creator.completedDeals} deal đã hoàn thành
              {creator.categories.length ? ` · ${creator.categories.join(', ')}` : ''}
            </div>
          </div>
        </div>
        <span className="ui-divider inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-[12px] font-semibold ui-text">
          {applicationStatusLabel(application.status)}
        </span>
      </div>

      {application.message && <p className="mt-2 text-sm ui-text">{application.message}</p>}
      {application.rejectReason && (
        <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">Lý do: {application.rejectReason}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[12px] ui-muted">
        <span>Gửi lúc: {formatDate(application.createdAt)}</span>
        {application.decidedAt && <span>Cập nhật: {formatDate(application.decidedAt)}</span>}
      </div>

      {application.status === 'Pending' && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy || locked}
            title={locked ? SANCTION_LOCK_HINT : undefined}
            onClick={onApprove}
            className="rounded-md bg-[#0057FF] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {busy ? 'Đang xử lý…' : 'Duyệt & đăng'}
          </button>
          <button
            type="button"
            disabled={busy || locked}
            title={locked ? SANCTION_LOCK_HINT : undefined}
            onClick={onNegotiate}
            className="rounded-md border ui-divider px-3 py-1.5 text-sm font-semibold ui-text disabled:opacity-60"
          >
            Thương lượng
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onReject}
            className="rounded-md border border-rose-200 px-3 py-1.5 text-sm font-semibold text-rose-600 disabled:opacity-60 dark:border-rose-900/40"
          >
            Từ chối
          </button>
        </div>
      )}
      <div className="mt-3">
        <BrandThreadToggle threadId={application.threadId} />
      </div>
    </div>
  );
};

const BrandCampaignApplications = React.forwardRef<BrandCampaignApplicationsHandle, BrandCampaignApplicationsProps>(
  ({ campaignId, onChanged, onNegotiate, onGoToRatingsTab }, ref) => {
    const [loading, setLoading] = useState(true);
    const [applications, setApplications] = useState<BrandApplicationForBusiness[]>([]);
    const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const { blocked } = useOrdererSanction();
    const inFlightRef = useRef<Set<string>>(new Set());

    const setBusy = (id: string, busy: boolean) =>
      setBusyIds((prev) => {
        const next = new Set(prev);
        if (busy) next.add(id);
        else next.delete(id);
        return next;
      });

    const load = useCallback(() => {
      setLoading(true);
      getCampaignApplications(campaignId)
        .then(setApplications)
        .catch(() => toast.error('Không tải được đơn xin tham gia.'))
        .finally(() => setLoading(false));
    }, [campaignId]);

    useEffect(() => {
      load();
    }, [load]);

    useImperativeHandle(ref, () => ({ reload: load }), [load]);

    const handleApprove = async (application: BrandApplicationForBusiness) => {
      if (inFlightRef.current.has(application.id)) return;
      if (
        !window.confirm(`Duyệt đơn của ${application.creator.displayName}? Bài sẽ tự đăng công khai ngay sau khi duyệt.`)
      )
        return;
      inFlightRef.current.add(application.id);
      setBusy(application.id, true);
      try {
        const result = await approveBrandApplication(application.id);
        if (result.message) {
          // Chưa chắc đã Live (deal còn Publishing, job nền sẽ thử lại) — toast thông tin, không
          // phải "thành công", để không báo nhầm trạng thái (cùng cách với brand-deals.page.tsx
          // handleAccept — M1 — cho đúng message "Đang đăng bài...").
          toast(result.message, { icon: 'ℹ️' });
        } else {
          toast.success(`Đã duyệt và đăng bài cho ${application.creator.displayName}.`);
        }
        load();
        onChanged();
      } catch (err) {
        showOrderErrorToast(err, 'Duyệt đơn thất bại.', onGoToRatingsTab);
      } finally {
        inFlightRef.current.delete(application.id);
        setBusy(application.id, false);
      }
    };

    const handleReject = async (application: BrandApplicationForBusiness) => {
      if (inFlightRef.current.has(application.id)) return;
      const input = window.prompt('Lý do từ chối (không bắt buộc, tối đa 200 ký tự):', '');
      if (input === null) return; // bấm Huỷ ở hộp thoại nhập lý do
      const reason = input.trim();
      if (reason.length > REJECT_REASON_MAX_LENGTH) {
        toast.error(`Lý do từ chối tối đa ${REJECT_REASON_MAX_LENGTH} ký tự.`);
        return;
      }
      if (!window.confirm(`Từ chối đơn của ${application.creator.displayName}?`)) return;
      inFlightRef.current.add(application.id);
      setBusy(application.id, true);
      try {
        await rejectBrandApplication(application.id, reason || undefined);
        toast.success('Đã từ chối đơn.');
        load();
        onChanged();
      } catch (err) {
        toast.error(errorMessage(err, 'Từ chối đơn thất bại.'));
      } finally {
        inFlightRef.current.delete(application.id);
        setBusy(application.id, false);
      }
    };

    if (loading) {
      return (
        <div className="flex justify-center p-8">
          <Loader2 className="animate-spin ui-muted" />
        </div>
      );
    }

    if (applications.length === 0) {
      return <p className="p-4 text-center text-sm ui-muted">Chưa có đơn xin tham gia nào.</p>;
    }

    return (
      <div className="space-y-2">
        {applications.map((application) => (
          <ApplicationCard
            key={application.id}
            application={application}
            busy={busyIds.has(application.id)}
              locked={blocked}
            onApprove={() => handleApprove(application)}
            onReject={() => handleReject(application)}
            onNegotiate={() => onNegotiate(application)}
          />
        ))}
      </div>
    );
  }
);

BrandCampaignApplications.displayName = 'BrandCampaignApplications';

export default BrandCampaignApplications;
